import { supabase } from '../lib/supabaseClient';
import type { ElementNature, RepresentativeMethod, ValueClass } from '../utils/chronoStats';

export type StudyStatus = 'draft' | 'active' | 'approved' | 'archived';
export type ResourceKind = 'operator' | 'machine';

export interface ChronoStudy {
  id: string;
  organizationId: string;
  projectId: string | null;
  processId: string | null;
  title: string;
  operationLabel: string;
  productFamily: string | null;
  demandQuantity: number | null;
  availableSeconds: number | null;
  status: StudyStatus;
  createdBy: string | null;
  createdAt: string;
}

export interface ChronoResource {
  id: string;
  studyId: string;
  kind: ResourceKind;
  name: string;
  sortOrder: number;
}

export interface ChronoElement {
  id: string;
  scenarioId: string;
  resourceId: string;
  sortOrder: number;
  description: string;
  representativeMethod: RepresentativeMethod;
  representativeSeconds: number | null;
  valueClass: ValueClass;
  nature: ElementNature;
  notes: string | null;
}

export interface ChronoObservation {
  id: string;
  elementId: string;
  sequenceNo: number;
  seconds: number;
}

export interface ChronoRelation {
  id: string;
  scenarioId: string;
  fromElementId: string;
  toElementId: string;
  relation: 'precedes' | 'simultaneous';
  lagSeconds: number;
}

export interface ChronoBundle {
  study: ChronoStudy;
  currentScenarioId: string;
  resources: ChronoResource[];
  elements: ChronoElement[];
  observations: ChronoObservation[];
  relations: ChronoRelation[];
}

export interface StudyInput {
  title: string;
  operationLabel: string;
  projectId?: string | null;
  processId?: string | null;
  productFamily?: string | null;
  demandQuantity?: number | null;
  availableSeconds?: number | null;
}

function num(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapStudy(row: Record<string, unknown>): ChronoStudy {
  return {
    id: String(row.id),
    organizationId: String(row.organization_id),
    projectId: row.project_id == null ? null : String(row.project_id),
    processId: row.process_id == null ? null : String(row.process_id),
    title: String(row.title),
    operationLabel: String(row.operation_label),
    productFamily: row.product_family == null ? null : String(row.product_family),
    demandQuantity: num(row.demand_quantity),
    availableSeconds: num(row.available_seconds),
    status: row.status as StudyStatus,
    createdBy: row.created_by == null ? null : String(row.created_by),
    createdAt: String(row.created_at),
  };
}

export async function listChronoStudies(organizationId: string): Promise<ChronoStudy[]> {
  const { data, error } = await supabase
    .from('chrono_studies')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => mapStudy(row as Record<string, unknown>));
}

export async function listChronoProjects(organizationId: string): Promise<Array<{ id: string; name: string }>> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, nome_projeto')
    .eq('organization_id', organizationId)
    .order('nome_projeto');
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: String(row.id), name: String(row.nome_projeto) }));
}

export async function listChronoProcesses(organizationId: string): Promise<Array<{ id: string; name: string }>> {
  const { data: departments, error: departmentError } = await supabase
    .from('org_departments')
    .select('id')
    .eq('organization_id', organizationId);
  if (departmentError) throw departmentError;
  const departmentIds = (departments ?? []).map((row) => row.id);
  if (departmentIds.length === 0) return [];
  const { data: sectors, error: sectorError } = await supabase
    .from('org_sectors')
    .select('id')
    .in('department_id', departmentIds);
  if (sectorError) throw sectorError;
  const sectorIds = (sectors ?? []).map((row) => row.id);
  if (sectorIds.length === 0) return [];
  const { data: processes, error: processError } = await supabase
    .from('org_processes')
    .select('id, name')
    .in('sector_id', sectorIds)
    .order('name');
  if (processError) throw processError;
  return (processes ?? []).map((row) => ({ id: String(row.id), name: String(row.name) }));
}

export async function createChronoStudy(
  organizationId: string,
  createdBy: string | null,
  input: StudyInput,
): Promise<string> {
  const { data: study, error } = await supabase
    .from('chrono_studies')
    .insert({
      organization_id: organizationId,
      created_by: createdBy,
      title: input.title.trim(),
      operation_label: input.operationLabel.trim(),
      project_id: input.projectId || null,
      process_id: input.processId || null,
      product_family: input.productFamily?.trim() || null,
      demand_quantity: input.demandQuantity ?? null,
      available_seconds: input.availableSeconds ?? null,
      status: 'draft',
    })
    .select('id')
    .single();
  if (error) throw error;
  const studyId = String(study.id);
  const { error: scenarioError } = await supabase.from('chrono_scenarios').insert({
    study_id: studyId,
    organization_id: organizationId,
    kind: 'current',
    name: 'Estado atual',
  });
  if (scenarioError) {
    await supabase.from('chrono_studies').delete().eq('id', studyId);
    throw scenarioError;
  }
  return studyId;
}

export async function updateChronoStudy(studyId: string, input: StudyInput): Promise<void> {
  const { error } = await supabase
    .from('chrono_studies')
    .update({
      title: input.title.trim(),
      operation_label: input.operationLabel.trim(),
      project_id: input.projectId || null,
      process_id: input.processId || null,
      product_family: input.productFamily?.trim() || null,
      demand_quantity: input.demandQuantity ?? null,
      available_seconds: input.availableSeconds ?? null,
    })
    .eq('id', studyId);
  if (error) throw error;
}

export async function loadChronoBundle(organizationId: string, studyId: string): Promise<ChronoBundle | null> {
  const { data: studyRow, error: studyError } = await supabase
    .from('chrono_studies')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('id', studyId)
    .maybeSingle();
  if (studyError) throw studyError;
  if (!studyRow) return null;

  const { data: scenarios, error: scenarioError } = await supabase
    .from('chrono_scenarios')
    .select('id, kind')
    .eq('study_id', studyId)
    .eq('kind', 'current')
    .limit(1);
  if (scenarioError) throw scenarioError;
  const currentScenarioId = scenarios?.[0]?.id;
  if (!currentScenarioId) throw new Error('O estudo não tem estado atual.');

  const [{ data: resources, error: resourceError }, { data: elements, error: elementError }] = await Promise.all([
    supabase.from('chrono_resources').select('*').eq('study_id', studyId).order('sort_order'),
    supabase.from('chrono_elements').select('*').eq('scenario_id', currentScenarioId).order('sort_order'),
  ]);
  if (resourceError) throw resourceError;
  if (elementError) throw elementError;
  const elementIds = (elements ?? []).map((row) => row.id);
  const { data: observations, error: observationError } = elementIds.length === 0
    ? { data: [], error: null }
    : await supabase.from('chrono_observations').select('*').in('element_id', elementIds).order('sequence_no');
  if (observationError) throw observationError;
  const { data: relations, error: relationError } = await supabase
    .from('chrono_element_relations')
    .select('*')
    .eq('scenario_id', currentScenarioId);
  if (relationError) throw relationError;

  return {
    study: mapStudy(studyRow as Record<string, unknown>),
    currentScenarioId: String(currentScenarioId),
    resources: (resources ?? []).map((row) => ({
      id: String(row.id),
      studyId: String(row.study_id),
      kind: row.kind as ResourceKind,
      name: String(row.name),
      sortOrder: Number(row.sort_order),
    })),
    elements: (elements ?? []).map((row) => ({
      id: String(row.id),
      scenarioId: String(row.scenario_id),
      resourceId: String(row.resource_id),
      sortOrder: Number(row.sort_order),
      description: String(row.description),
      representativeMethod: row.representative_method as RepresentativeMethod,
      representativeSeconds: num(row.representative_seconds),
      valueClass: row.value_class as ValueClass,
      nature: row.nature as ElementNature,
      notes: row.notes == null ? null : String(row.notes),
    })),
    observations: (observations ?? []).map((row) => ({
      id: String(row.id),
      elementId: String(row.element_id),
      sequenceNo: Number(row.sequence_no),
      seconds: Number(row.seconds),
    })),
    relations: (relations ?? []).map((row) => ({
      id: String(row.id),
      scenarioId: String(row.scenario_id),
      fromElementId: String(row.from_element_id),
      toElementId: String(row.to_element_id),
      relation: row.relation as ChronoRelation['relation'],
      lagSeconds: Number(row.lag_seconds),
    })),
  };
}

export async function addChronoResource(
  organizationId: string,
  studyId: string,
  kind: ResourceKind,
  name: string,
  sortOrder: number,
): Promise<void> {
  const { error } = await supabase.from('chrono_resources').insert({
    organization_id: organizationId,
    study_id: studyId,
    kind,
    name: name.trim(),
    sort_order: sortOrder,
  });
  if (error) throw error;
}

export async function removeChronoResource(resourceId: string): Promise<void> {
  const { error } = await supabase.from('chrono_resources').delete().eq('id', resourceId);
  if (error) throw error;
}

export async function addChronoElement(input: {
  organizationId: string;
  studyId: string;
  scenarioId: string;
  resourceId: string;
  description: string;
  sortOrder: number;
}): Promise<void> {
  const { error } = await supabase.from('chrono_elements').insert({
    organization_id: input.organizationId,
    study_id: input.studyId,
    scenario_id: input.scenarioId,
    resource_id: input.resourceId,
    description: input.description.trim(),
    sort_order: input.sortOrder,
  });
  if (error) throw error;
}

export async function updateChronoElement(elementId: string, patch: {
  description?: string;
  resourceId?: string;
  nature?: ElementNature;
  valueClass?: ValueClass;
  sortOrder?: number;
  representativeMethod?: RepresentativeMethod;
  representativeSeconds?: number | null;
}): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.description != null) row.description = patch.description.trim();
  if (patch.resourceId != null) row.resource_id = patch.resourceId;
  if (patch.nature != null) row.nature = patch.nature;
  if (patch.valueClass != null) row.value_class = patch.valueClass;
  if (patch.sortOrder != null) row.sort_order = patch.sortOrder;
  if (patch.representativeMethod != null) {
    row.representative_method = patch.representativeMethod;
    row.representative_seconds = patch.representativeMethod === 'manual' ? patch.representativeSeconds : null;
  }
  const { error } = await supabase.from('chrono_elements').update(row).eq('id', elementId);
  if (error) throw error;
}

export async function removeChronoElement(elementId: string): Promise<void> {
  const { error } = await supabase.from('chrono_elements').delete().eq('id', elementId);
  if (error) throw error;
}

export async function addChronoObservations(input: {
  organizationId: string;
  studyId: string;
  rows: Array<{ elementId: string; sequenceNo: number; seconds: number }>;
}): Promise<void> {
  if (input.rows.length === 0) return;
  const { error } = await supabase.from('chrono_observations').insert(input.rows.map((row) => ({
    organization_id: input.organizationId,
    study_id: input.studyId,
    element_id: row.elementId,
    sequence_no: row.sequenceNo,
    seconds: row.seconds,
  })));
  if (error) throw error;
  await supabase.from('chrono_studies').update({ status: 'active' }).eq('id', input.studyId).eq('status', 'draft');
}

export async function replaceChronoRelation(input: {
  organizationId: string;
  studyId: string;
  scenarioId: string;
  toElementId: string;
  fromElementId: string;
  relation: ChronoRelation['relation'];
  lagSeconds: number;
}): Promise<void> {
  const { error: deleteError } = await supabase
    .from('chrono_element_relations')
    .delete()
    .eq('scenario_id', input.scenarioId)
    .eq('to_element_id', input.toElementId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('chrono_element_relations').insert({
    organization_id: input.organizationId,
    study_id: input.studyId,
    scenario_id: input.scenarioId,
    from_element_id: input.fromElementId,
    to_element_id: input.toElementId,
    relation: input.relation,
    lag_seconds: input.lagSeconds,
  });
  if (error) throw error;
}

export async function clearChronoRelations(scenarioId: string, toElementId: string): Promise<void> {
  const { error } = await supabase
    .from('chrono_element_relations')
    .delete()
    .eq('scenario_id', scenarioId)
    .eq('to_element_id', toElementId);
  if (error) throw error;
}

export async function removeChronoObservation(observationId: string): Promise<void> {
  const { error } = await supabase.from('chrono_observations').delete().eq('id', observationId);
  if (error) throw error;
}
